/**
 * Admin-authored ("custom") reviews come from our own backend as plain JSON
 * (see server/api/user-reviews.js). These helpers shape them like the
 * denormalised Sharetribe review entities the UI already renders, so the
 * profile and listing pages can show one seamless, date-sorted list.
 */
import { types as sdkTypes } from './sdkLoader';
import { REVIEW_TYPE_OF_PROVIDER } from './types';

const { UUID } = sdkTypes;

// "Sarah M." -> "SM" — same idea as Sharetribe's abbreviatedName, used for
// the initials avatar since custom review authors have no profile image.
// Also used by the admin Reviews page for expert avatars without an image.
export const abbreviateName = displayName => {
  const parts = (displayName || '')
    .split(/\s+/)
    .filter(part => part.length > 0)
    .map(part => part.charAt(0).toUpperCase());
  return parts.slice(0, 2).join('');
};

/**
 * Convert one custom review (API JSON) into a Sharetribe-shaped review entity.
 * The author is synthetic: it has no id, which the UI uses to render it
 * without a profile link.
 */
export const customReviewToEntity = review => ({
  id: new UUID(review.id),
  type: 'review',
  attributes: {
    type: REVIEW_TYPE_OF_PROVIDER,
    state: 'public',
    deleted: false,
    rating: review.rating,
    content: review.content,
    createdAt: new Date(review.createdAt),
  },
  author: {
    id: null,
    type: 'user',
    attributes: {
      banned: false,
      deleted: false,
      profile: {
        displayName: review.authorDisplayName,
        abbreviatedName: abbreviateName(review.authorDisplayName),
        publicData: {},
      },
    },
    profileImage: null,
  },
});

/**
 * Merge Sharetribe review entities with custom review entities into one list,
 * newest first — indistinguishable from a single review source.
 */
export const mergeReviews = (sharetribeReviews = [], customReviews = []) => {
  return [...sharetribeReviews, ...customReviews].sort(
    (a, b) => new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt)
  );
};
